import { useQuery } from "@tanstack/react-query";
import { type JSX, useEffect, useRef, useState } from "react";

import { CHIP } from "@/lib/class-presets";
import { cn } from "@/lib/utils";

import { apiFetch } from "./api";
import type { StatsResponse } from "./models";

interface ShellMetricValues {
  readonly active: number;
  readonly queued: number;
  readonly spend: string;
}

function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(document.visibilityState === "visible");
  useEffect(function watchVisibility(): () => void {
    function visibilityChanged(): void {
      setVisible(document.visibilityState === "visible");
    }
    document.addEventListener("visibilitychange", visibilityChanged);
    return function stopWatchingVisibility(): void {
      document.removeEventListener("visibilitychange", visibilityChanged);
    };
  }, []);
  return visible;
}

function statsPath(): string {
  const to = new Date();
  const from = new Date(
    Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate(), 0, 0, 0, 0),
  );
  const query = new URLSearchParams({
    scope: "global",
    from: from.toISOString(),
    to: to.toISOString(),
    bucket: "hour",
  });
  return `/stats?${query.toString()}`;
}

function metricValues(stats: StatsResponse): ShellMetricValues {
  const activeStatuses = ["queued", "starting", "running", "waiting"];
  let active = 0;
  for (const status of activeStatuses) active += stats.runsByStatus[status] ?? 0;
  let spend = 0;
  for (const bucket of stats.series) spend += Number(bucket.costUsd);
  return {
    active,
    queued: stats.runsByStatus["queued"] ?? 0,
    spend: Number.isFinite(spend) ? spend.toFixed(6) : "0.000000",
  };
}

export function ShellMetrics(): JSX.Element {
  const visible = useDocumentVisible();
  const wasVisible = useRef(visible);
  const stats = useQuery({
    queryKey: ["shell", "metrics"],
    queryFn: function fetchShellMetrics(): Promise<StatsResponse> {
      return apiFetch(statsPath());
    },
    refetchInterval: visible ? 30_000 : false,
    retry: false,
  });
  const refetch = stats.refetch;
  useEffect(
    function refetchAfterVisibilityRestored(): void {
      const becameVisible = visible && !wasVisible.current;
      wasVisible.current = visible;
      if (becameVisible) void refetch();
    },
    [refetch, visible],
  );
  const values = stats.data === undefined ? undefined : metricValues(stats.data);
  const stale = stats.isError || stats.isRefetchError;
  return (
    <div aria-label="Operational metrics" className="flex flex-wrap items-center justify-end gap-2">
      {/*
        A header narrower than 28rem (a phone) cannot hold the full label, a
        stale badge, the help action and a readable current crumb in one row,
        so the parenthetical becomes screen-reader text there: it still reads
        "Active (incl. queued)", it just shows "Active".
      */}
      <span className={CHIP}>
        Active<span className="@max-md/app-header:sr-only"> (incl. queued)</span>{" "}
        <strong>{values === undefined ? "—" : String(values.active)}</strong>
      </span>
      {/*
        The header is one fixed row (#163 R7), and three chips plus the
        breadcrumb only fit a wide one. The chips step aside by the width of
        the header itself (the `app-header` container in AppHeader), not the
        viewport, because the desktop sidebar narrows it from 768px up. Active
        always stays: the queued count is already inside it, and today's spend
        is the figure the dashboard repeats in full. So Queued needs a 42rem
        header and Today a 48rem one.
      */}
      <span className={cn(CHIP, "hidden @2xl/app-header:inline-flex")}>
        Queued <strong>{values === undefined ? "—" : String(values.queued)}</strong>
      </span>
      <span className={cn(CHIP, "hidden @3xl/app-header:inline-flex")}>
        Today <strong>{values === undefined ? "—" : `$${values.spend}`}</strong>
      </span>
      {stale && (
        <span className="inline-flex items-center gap-1.5 border border-status-waiting/50 bg-status-waiting-muted px-2 py-1 text-xs uppercase text-status-waiting-foreground">
          stale
        </span>
      )}
    </div>
  );
}
